import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const FLAG_TYPE_LABELS = {
  gone_quiet: 'Client Gone Quiet',
  ar_and_active: 'Active Work + Overdue AR',
  zero_revenue: 'Completed Job — No Revenue',
  under_billed: 'Revenue Below Estimate',
  stale_jobs: 'Stale Open Jobs',
  month_end_bunching: 'Month-End Job Bunching',
  cost_no_revenue: 'Cost Booked — No Revenue',
  critical_ar: 'Critical AR Outstanding',
  low_cash: 'Low Cash Position',
};

function fmt(v) {
  return (parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function downloadJobsExcel(flag) {
  const jobs = flag.jobs || [];
  const clients = flag.clients || [];
  const label = FLAG_TYPE_LABELS[flag.type] || flag.type;
  const today = new Date().toISOString().split('T')[0];
  const filename = `Trejhara_${flag.type}_${flag.clientName ? flag.clientName.replace(/\s+/g, '_') + '_' : ''}${today}.xlsx`;

  const wb = XLSX.utils.book_new();

  if (jobs.length > 0) {
    const rows = jobs.map(j => ({
      'Job No': j.jobNo,
      'Booking No': j.bookingNo,
      'Client': j.client,
      'Responsible Person': j.responsiblePerson || '—',
      'Job Type': j.jobType,
      'Office': j.office,
      'Status': j.status,
      'Days Open': j.daysOpen,
      'Est Revenue': j.estRev || 0,
      'Act Revenue': j.actRev || 0,
      'Act Cost': j.actCost || 0,
      'P&L': j.pl || 0,
      'GP%': j.gpPct != null ? parseFloat(j.gpPct.toFixed(1)) : '',
      'Currency': j.currency || 'AED',
      'Flag': label,
      'Action Required': flag.action,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 22 }, { wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 28 }, { wch: 36 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Jobs');
  }

  if (clients.length > 0) {
    const rows = clients.map(r => ({
      'Client': r.client,
      'Total Pending': r.pending || 0,
      '<60 days': r.lt60 || 0,
      '60-90 days': r.d60_90 || 0,
      '90-180 days': r.d90_180 || 0,
      '180-365 days': r.d180_365 || 0,
      '>365 days': r.gt365 || 0,
      'Flag': label,
      'Action Required': flag.action,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 36 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'AR Clients');
  }

  // Summary sheet
  const summary = [{
    'Generated': new Date().toLocaleString(),
    'Flag Type': label,
    'Client': flag.clientName || 'All',
    'Message': flag.message,
    'Action Required': flag.action,
    'Total Items': jobs.length || clients.length,
  }];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary');

  XLSX.writeFile(wb, filename);
}

// ─── JOB DRILL-DOWN MODAL ────────────────────────────────────────────────────
function JobDrillDown({ flag, onClose }) {
  const jobs = flag.jobs || [];
  const clients = flag.clients || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{FLAG_TYPE_LABELS[flag.type] || flag.type}</div>
            {flag.clientName && <div className="modal-subtitle">{flag.clientName}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn-primary"
              style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={() => downloadJobsExcel(flag)}
            >
              ↓ Download Excel
            </button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="flag-drill-message">{flag.message}</div>

        {/* Job list */}
        {jobs.length > 0 && (
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job No</th>
                  <th>Client</th>
                  <th>Responsible</th>
                  <th>Type</th>
                  <th className="right">Days Open</th>
                  <th className="right">Est Rev</th>
                  <th className="right">Act Rev</th>
                  <th className="right">Act Cost</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{j.jobNo}</td>
                    <td>{j.client}</td>
                    <td style={{ color: j.responsiblePerson && j.responsiblePerson !== '—' ? '#0e7c7b' : '#9ca3af', fontSize: 12, fontWeight: 600 }}>
                      {j.responsiblePerson || '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{j.jobType}</td>
                    <td className={`right ${j.daysOpen > 90 ? 'danger' : j.daysOpen > 60 ? 'warn' : ''}`}>
                      {j.daysOpen}d
                    </td>
                    <td className="right">{j.estRev > 0 ? fmt(j.estRev) : '—'}</td>
                    <td className={`right ${j.actRev === 0 ? 'danger' : 'good'}`}>
                      {j.actRev > 0 ? fmt(j.actRev) : '—'}
                    </td>
                    <td className={`right ${j.actCost > 0 && j.actRev === 0 ? 'danger' : ''}`}>
                      {j.actCost > 0 ? fmt(j.actCost) : '—'}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11, padding: '2px 6px', borderRadius: 4,
                        background: j.status === 'Completed' ? '#dcfce7' : '#fef3c7',
                        color: j.status === 'Completed' ? '#166534' : '#92400e',
                      }}>
                        {j.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AR client list for critical_ar flag */}
        {clients.length > 0 && (
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th className="right">Total Pending</th>
                  <th className="right">90-180d</th>
                  <th className="right">180-365d</th>
                  <th className="right">&gt;365d</th>
                </tr>
              </thead>
              <tbody>
                {clients.sort((a, b) => (b.d90_180 + b.gt365) - (a.d90_180 + a.gt365)).map((r, i) => (
                  <tr key={i} className={r.gt365 > 0 ? 'row-danger' : 'row-warn'}>
                    <td>{r.client}</td>
                    <td className="right bold">{fmt(r.pending)}</td>
                    <td className={`right ${r.d90_180 > 0 ? 'danger' : ''}`}>{r.d90_180 > 0 ? fmt(r.d90_180) : '—'}</td>
                    <td className={`right ${r.d180_365 > 0 ? 'danger' : ''}`}>{r.d180_365 > 0 ? fmt(r.d180_365) : '—'}</td>
                    <td className={`right ${r.gt365 > 0 ? 'danger' : ''}`}>{r.gt365 > 0 ? fmt(r.gt365) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flag-drill-action">→ {flag.action}</div>
      </div>
    </div>
  );
}

// ─── MAIN FLAGS VIEW ─────────────────────────────────────────────────────────
export default function FlagsView({ globalFlags, clientMetrics, entity }) {
  const [dismissed, setDismissed] = useState({});
  const [drillFlag, setDrillFlag] = useState(null);

  const clientFlags = clientMetrics.flatMap(c =>
    c.flags.map(f => ({ ...f, clientName: c.name, responsible: c.responsible }))
  );

  const allFlags = [
    ...globalFlags.map(f => ({ ...f, source: 'system' })),
    ...clientFlags.map(f => ({ ...f, source: 'client' })),
  ].filter(f => !dismissed[`${f.type}-${f.clientName || 'global'}`]);

  const redFlags = allFlags.filter(f => f.severity === 'red');
  const amberFlags = allFlags.filter(f => f.severity === 'amber');

  const dismiss = (flag) => {
    const key = `${flag.type}-${flag.clientName || 'global'}`;
    setDismissed(prev => ({ ...prev, [key]: true }));
  };

  const hasDrillDown = (flag) => flag.jobs?.length > 0 || flag.clients?.length > 0;

  const FlagCard = ({ flag }) => (
    <div className={`flag-card flag-${flag.severity}`}>
      <div className="flag-header">
        <span className={`flag-severity-badge ${flag.severity}`}>
          {flag.severity === 'red' ? '🔴 Act Now' : '🟡 Watch'}
        </span>
        {flag.clientName && <span className="flag-client">{flag.clientName}</span>}
        {flag.responsible && <span className="flag-owner">→ {flag.responsible}</span>}
        <span className="flag-type-label">{FLAG_TYPE_LABELS[flag.type] || flag.type}</span>
      </div>
      <p className="flag-message">{flag.message}</p>
      <div className="flag-footer">
        <span className="flag-action-text">→ {flag.action}</span>
        <div className="flag-actions">
          {hasDrillDown(flag) && (
            <button
              className="btn-flag-drill"
              onClick={() => setDrillFlag(flag)}
            >
              View {flag.jobs?.length || flag.clients?.length} {flag.jobs ? 'jobs' : 'clients'} →
            </button>
          )}
          <button className="btn-flag-dismiss" onClick={() => dismiss(flag)}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );

  if (allFlags.length === 0) {
    return (
      <div className="view flags-view">
        <div className="all-clear">
          <div className="all-clear-icon">✅</div>
          <h2>All clear</h2>
          <p>No flags requiring attention right now.</p>
          {Object.keys(dismissed).length > 0 && (
            <button className="btn-secondary" onClick={() => setDismissed({})}>
              Show {Object.keys(dismissed).length} dismissed flags
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="view flags-view">
      {drillFlag && (
        <JobDrillDown flag={drillFlag} onClose={() => setDrillFlag(null)} />
      )}

      <div className="flags-header">
        <div className="flags-summary">
          {redFlags.length > 0 && (
            <span className="flags-count red">🔴 {redFlags.length} need action today</span>
          )}
          {amberFlags.length > 0 && (
            <span className="flags-count amber">🟡 {amberFlags.length} to watch</span>
          )}
        </div>
        {Object.keys(dismissed).length > 0 && (
          <button className="btn-secondary small" onClick={() => setDismissed({})}>
            Restore {Object.keys(dismissed).length} dismissed
          </button>
        )}
      </div>

      {redFlags.length > 0 && (
        <div className="flag-section">
          <h3 className="flag-section-title red">🔴 Act Today</h3>
          <div className="flag-cards">
            {redFlags.map((f, i) => <FlagCard key={i} flag={f} />)}
          </div>
        </div>
      )}

      {amberFlags.length > 0 && (
        <div className="flag-section">
          <h3 className="flag-section-title amber">🟡 Watch These</h3>
          <div className="flag-cards">
            {amberFlags.map((f, i) => <FlagCard key={i} flag={f} />)}
          </div>
        </div>
      )}
    </div>
  );
}
